import dice from "../../assets/icons/dice.svg";
import check from "../../assets/icons/check.svg";
import clear from "../../assets/icons/clear.svg";
import { TicketMapper } from "../../models/TicketMapper";
import Badge from "./Badge";
import { DrawInfo } from "../../models/DrawInfo";
import useHover from "../../hooks/useHover";
import { useRef } from "react";
import { LUCKY_DIP_LONG_PRESS_MS } from "../../utils/Constants";

export interface TicketCardProps {
  onClickNumber: (number: number, selected: boolean) => void;
  onClickStar: (star: number, selected: boolean) => void;
  onLuckyDip: () => void;
  onLuckyDipAll: () => void;
  onClear: () => void;
  ticket: TicketMapper;
  drawInfo?: DrawInfo;
  pulsing?: boolean;
}

export default function TicketCard(props: TicketCardProps) {
  const { ref: luckyDipRef, isHovered: isLuckyDipHovered } =
    useHover<HTMLButtonElement>();
  const { ref: clearTicketRef, isHovered: isClearTicketHovered } =
    useHover<HTMLButtonElement>();
  const allNumbersSelected: boolean = props.ticket.areAllNumbersSelected(
    Number(props.drawInfo?.drawConfig.numbersPerTicket ?? 0n),
  );
  const allStarsSelected: boolean = props.ticket.areAllStarsSelected(
    Number(props.drawInfo?.drawConfig.starsPerTicket ?? 0n),
  );
  const numbersPerTicket: number = Number(
    props.drawInfo?.drawConfig.numbersPerTicket ?? 0n,
  );
  const starsPerTicket: number = Number(
    props.drawInfo?.drawConfig.starsPerTicket ?? 0n,
  );
  const isTicketComplete: boolean = props.ticket.isComplete(
    numbersPerTicket,
    starsPerTicket,
  );
  const isTicketStarted: boolean = props.ticket.isStarted();
  const numberBadges = props.ticket.numbers.map((selected, index) => (
    <Badge
      key={index}
      selected={selected}
      pulsing={selected && props.pulsing}
      onSelect={
        !allNumbersSelected || selected
          ? function (selected: boolean): void {
            props.onClickNumber(index, selected);
          }
          : undefined
      }
      isStar={false}
      isGrayedOut={allNumbersSelected && !selected}
    >
      {index + 1}
    </Badge>
  ));
  const starBadges = props.ticket.stars.map((selected, index) => (
    <Badge
      key={index}
      selected={selected}
      pulsing={selected && props.pulsing}
      onSelect={
        !allStarsSelected || selected
          ? function (selected: boolean): void {
            props.onClickStar(index, selected);
          }
          : undefined
      }
      isStar={true}
      isGrayedOut={allStarsSelected && !selected}
    >
      {index + 1}
    </Badge>
  ));

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseDown = () => {
    timerRef.current = setTimeout(() => {
      props.onLuckyDipAll();
      timerRef.current = null;
    }, LUCKY_DIP_LONG_PRESS_MS);
  };

  const handleMouseUp = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  return (
    <div
      className={`relative rounded-lg transition duration-250 delay-100 ${isTicketComplete ? "ticket-gold-highlight-border scale-[1.05]" : isTicketStarted ? "ticket-silver-highlight-border scale-[1.025]" : "ticket-idle-border"}`}
    >
      <div className="bg-bastille rounded-lg p-2 select-none">
        <div className="flex flex-row justify-between items-center select-none mb-1 px-1">
          <div className="self-stretch flex flex-row items-center">
            {allNumbersSelected ? (
              <div className="flex flex-row items-center">
                <p className="whitespace-nowrap font-barlowc font-bold text-sm bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
                  {numbersPerTicket} NUMBERS
                </p>
                <img src={check} className="pl-1.5 h-3" alt="check" />
              </div>
            ) : (
              <p className="whitespace-nowrap font-barlowc font-bold text-sm text-gray">
                {numbersPerTicket} NUMBERS
              </p>
            )}
          </div>
          <div className="flex flex-row">
            <button
              ref={luckyDipRef}
              onClick={props.onLuckyDip}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
              onTouchCancel={handleMouseLeave}
              className={"flex flex-row items-center pr-1"}
            >
              <img src={dice} className="h-4" alt="dice" />
              <div
                className={`pl-1 whitespace-nowrap font-barlowc font-bold text-sm bg-gradient-to-b from-persian-pink to-cadillac bg-clip-text text-transparent overflow-hidden transition-all duration-500 ${isLuckyDipHovered ? "max-w-xs" : "max-w-0"}`}
              >
                LUCKY DIP
              </div>
            </button>
            <button
              ref={clearTicketRef}
              onClick={props.onClear}
              className={"flex flex-row items-center"}
            >
              <img src={clear} className="h-4" alt="clear" />
              <div
                className={`whitespace-nowrap font-barlowc font-bold text-sm bg-gradient-to-b from-valencia to-thunderbird bg-clip-text text-transparent overflow-hidden transition-all duration-500 ${isClearTicketHovered ? "max-w-xs" : "max-w-0"}`}
              >
                CLEAR
              </div>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-8">{numberBadges}</div>
      </div>
      <div className="bg-transparent ticket-dashed-border">
        <div className="bg-bastille rounded-lg p-2 select-none">
          <div className="self-stretch flex flex-row items-center select-none mb-1 px-1">
            {allStarsSelected ? (
              <div className="flex flex-row items-center">
                <p className="whitespace-nowrap font-barlowc font-bold text-sm bg-gradient-to-b from-mango to-crail bg-clip-text text-transparent">
                  {starsPerTicket} STARS
                </p>
                <img src={check} className="pl-1.5 h-3" alt="check" />
              </div>
            ) : (
              <p className="whitespace-nowrap font-barlowc font-bold text-sm text-gray">
                {starsPerTicket} STARS
              </p>
            )}
          </div>
          <div className="grid grid-cols-8">{starBadges}</div>
        </div>
      </div>
    </div>
  );
}
